import { useCallback, useEffect, useRef, useState } from "react";
import OpenSeadragon from "openseadragon";
import "./OpenSeaDragonViewer.css";
import { addDragMouseEvents, makeEdgesAndCorners, unSelectOverlay } from "./openseadragon_utils";

const useOpenSeaDragon = ({ regions, tileSources }) => {
  const currentPageNumberRef = useRef(1);

  const [pageNumberAtScroll, setPageNumberAtScroll] = useState(1);
  const pageNumberAtScrollRef = useRef(pageNumberAtScroll);
  pageNumberAtScrollRef.current = pageNumberAtScroll; // Keep ref updated with state

  const [loadingViewer, setLoadingViewer] = useState(true);
  const loadingViewerRef = useRef(loadingViewer);
  loadingViewerRef.current = loadingViewer;

  const viewerRef = useRef();
  const selectedOverlay = useRef();
  const collectionTileMargin = 10;
  const collectionTileSize = 800;

  // Memoize functions to prevent recreation on each render
  const movePage = useCallback((x, y) => {
    if (!viewerRef.current || !viewerRef.current.viewport) return;

    viewerRef.current.viewport.panBy(
      viewerRef.current.viewport.deltaPointsFromPixels(new OpenSeadragon.Point(x, y), true)
    );
    viewerRef.current.viewport.applyConstraints();
  }, []);

  // viewer level key events
  const handleKeyPress = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleCanvasScroll = useCallback((event) => {
    event.preventDefault();
  }, []);

  // document level events
  const onContextMenu = useCallback((event) => {
    if (event.target.closest(".openseadragon-canvas")) {
      event.preventDefault();
    }
  }, []);

  const setPageNumber = useCallback(
    (page) => {
      if (!viewerRef.current || !viewerRef.current.world) return;

      const viewerItem = viewerRef.current.world.getItemAt(page - 1);
      if (!viewerItem) return;

      const tileCenter = viewerItem.getBounds().getCenter();
      const constraints = viewerRef.current.viewport.getConstrainedBounds();
      const pointY =
        (page - 1) * (collectionTileSize + collectionTileMargin) + constraints.height / 2 - 20;
      viewerRef.current.viewport.panTo(new OpenSeadragon.Point(tileCenter.x, pointY), true);
      setPageNumberAtScroll(page);
    },
    [collectionTileMargin, collectionTileSize]
  );

  const handleWheel = useCallback(
    (event) => {
      if (event.ctrlKey) {
        event.preventDefault();
      }
      if (event.target.closest(".openseadragon-canvas")) {
        event.preventDefault();
        if (event.ctrlKey || event.metaKey) {
          // This block intentionally left empty
          // Zoom functionality commented out but structure kept for future use
          /*
        const factor = Math.pow(1.1, event.wheelDelta < 0 ? -1 : 1);
        handleZoom(factor, viewerRef.current.viewport.pointFromPixel(new OpenSeadragon.Point(event.offsetX, event.offsetY), true));
        */
        } else {
          const { deltaY } = event;
          movePage(0, deltaY);
        }
      }
    },
    [movePage]
  );

  const keyDown = useCallback(
    (event) => {
      const REPEAT_EXCLUSION_LIST = [
        "arrowleft",
        "arrowright",
        "arrowup",
        "arrowdown",
        "+",
        "-",
        "_",
        "=",
      ];
      if (
        loadingViewerRef.current ||
        (event.repeat && !REPEAT_EXCLUSION_LIST.includes(event.key.toLowerCase()))
      )
        return;
      if (event.target.tagName === "INPUT") return;
      switch (event.key.toLowerCase()) {
        case "pageup":
          if (pageNumberAtScrollRef.current > 1) setPageNumber(pageNumberAtScrollRef.current - 1);
          break;
        case "pagedown":
          if (
            viewerRef.current &&
            viewerRef.current.world &&
            pageNumberAtScrollRef.current < viewerRef.current.world.getItemCount()
          ) {
            setPageNumber(pageNumberAtScrollRef.current + 1);
          }
          break;
        case "arrowleft":
          movePage(-40, 0);
          break;
        case "arrowdown":
          movePage(0, 40);
          break;
        case "arrowright":
          movePage(40, 0);
          break;
        case "arrowup":
          movePage(0, -40);
          break;
        case "escape":
          if (selectedOverlay?.current) unSelectOverlay(viewerRef, selectedOverlay);
          break;
        default:
          break;
      }
      event.preventDefault();
    },
    [movePage, setPageNumber]
  );

  const addExistingOverlays = useCallback(() => {
    if (
      !regions ||
      !tileSources ||
      !viewerRef.current ||
      viewerRef.current?.world?.getItemCount() === 0
    )
      return;

    regions.forEach((region, i) => {
      if (region.pageNumber > tileSources.length) return;
      const overlayElement = document.createElement("a");
      overlayElement.dataset.pageNumber = region.pageNumber + 1;
      overlayElement.dataset.confidence = region.confidence;
      overlayElement.dataset.fieldId = region.fieldId;
      overlayElement.dataset.fieldType = region.type;
      overlayElement.dataset.name = region.name;
      makeEdgesAndCorners(overlayElement);
      overlayElement.className = "overlay";
      overlayElement.classList.add("manual-overlay");
      overlayElement.id = region.id;

      // Add field name as text in the center of the overlay
      const nameSpan = document.createElement("span");
      nameSpan.className = "overlay-field-name";
      nameSpan.textContent = region.name || region.fieldId || "Field";
      nameSpan.style.position = "absolute";
      nameSpan.style.top = "50%";
      nameSpan.style.left = "50%";
      nameSpan.style.transform = "translate(-50%, -50%)";
      nameSpan.style.color = "#000";
      nameSpan.style.textShadow = "2px 2px 2px rgba(255,255,255,0.8)";
      nameSpan.style.fontSize = "12px";
      nameSpan.style.fontWeight = "bold";
      nameSpan.style.pointerEvents = "none"; // Prevent interference with drag events
      overlayElement.appendChild(nameSpan);

      const viewerItem = viewerRef.current?.world?.getItemAt(region.pageNumber);
      if (!viewerItem) return;
      const viewportRect = viewerItem.imageToViewportRectangle(
        region.x * viewerItem.getContentSize().x,
        region.y * viewerItem.getContentSize().y,
        region.w * viewerItem.getContentSize().x,
        region.h * viewerItem.getContentSize().y
      );
      const location = new OpenSeadragon.Rect(
        viewportRect.x,
        viewportRect.y,
        viewportRect.width,
        viewportRect.height
      );
      viewerRef.current?.addOverlay(overlayElement, location);
      const overlay = viewerRef.current?.getOverlayById(region.id);
      if (overlay && viewerRef.current) {
        addDragMouseEvents(viewerRef.current, overlayElement, overlay, selectedOverlay);
      }
    });
  }, [regions, tileSources]);

  const handleViewerLoaded = useCallback(() => {
    setPageNumber(1);
    addExistingOverlays();
    setLoadingViewer(false);
  }, [setPageNumber, addExistingOverlays]);

  const updatePageNumberAtScroll = useCallback(() => {
    if (!viewerRef.current || !viewerRef.current.viewport || !viewerRef.current.world) return;

    const viewportBounds = viewerRef.current.viewport.getBounds();
    const count = viewerRef.current.world.getItemCount();
    let i;
    let tiledImage;
    let imageBounds;
    let intersection;
    let area;
    let imageArea;
    let visibleFactor;
    let centerDistance;
    let best;
    for (i = 0; i < count; i += 1) {
      tiledImage = viewerRef.current.world.getItemAt(i);
      imageBounds = tiledImage.getBounds();
      intersection = viewportBounds.intersection(imageBounds);
      if (intersection) {
        area = intersection.width * intersection.height;
        imageArea = imageBounds.width * imageBounds.height;
        visibleFactor = area / imageArea;
        centerDistance = imageBounds.getCenter().distanceTo(viewportBounds.getCenter());
        if (
          !best ||
          visibleFactor > best.visibleFactor ||
          (visibleFactor === best.visibleFactor && centerDistance < best.centerDistance)
        ) {
          best = {
            visibleFactor,
            centerDistance,
            index: i,
          };
        }
      }
    }
    if (best && best.index !== pageNumberAtScrollRef.current - 1) {
      pageNumberAtScrollRef.current = best.index + 1;
      setPageNumberAtScroll(best.index + 1);
    }
  }, []);

  // Initialize viewer when tileSources changes
  useEffect(() => {
    if (!tileSources || tileSources.length === 0) {
      return undefined;
    }

    const viewerInstance = OpenSeadragon({
      id: "viewer",
      // prefixUrl: process.env.PUBLIC_URL + "/openseadragon/",
      collectionMode: true,
      collectionRows: 0,
      collectionColumns: 1,
      collectionTileMargin,
      collectionTileSize,
      homeFillsViewer: true,
      showHomeControl: false,
      showSequenceControl: false,
      showFullPageControl: false,
      mouseNavEnabled: true,
      minZoomImageRatio: 0.3,
      maxZoomPixelRatio: 3,
      visibilityRatio: 1,
      gestureSettingsMouse: {
        clickToZoom: false,
      },
      // toolbar: 'OSDToolbar',
      zoomInButton: "OSDZoomIn",
      zoomOutButton: "OSDZoomOut",
      tileSources,
    });

    viewerRef.current = viewerInstance;

    // Make the viewer instance accessible globally for the save function
    const viewerElement = document.getElementById("viewer");
    if (viewerElement) {
      const canvasParent = viewerElement.querySelector(".openseadragon-canvas")?.parentNode;
      if (canvasParent) {
        // Store the viewer instance on the parent node for external access
        canvasParent.__openSeadragonViewer = viewerInstance;
      }
    }

    viewerInstance.addHandler("canvas-key", handleKeyPress);
    viewerInstance.addHandler("canvas-scroll", handleCanvasScroll);
    viewerInstance.addHandler("open", handleViewerLoaded);
    viewerInstance.addHandler("zoom", updatePageNumberAtScroll);
    viewerInstance.addHandler("pan", updatePageNumberAtScroll);

    return () => {
      if (viewerRef.current) {
        viewerRef.current.removeHandler("canvas-key", handleKeyPress);
        viewerRef.current.removeHandler("canvas-scroll", handleCanvasScroll);
        viewerRef.current.removeHandler("open", handleViewerLoaded);
        viewerRef.current.removeHandler("zoom", updatePageNumberAtScroll);
        viewerRef.current.removeHandler("pan", updatePageNumberAtScroll);
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
    };
  }, [
    tileSources,
    handleKeyPress,
    handleCanvasScroll,
    handleViewerLoaded,
    updatePageNumberAtScroll,
    collectionTileMargin,
    collectionTileSize,
  ]);

  // Set up event listeners for document events
  useEffect(() => {
    const handler = (e) => {
      if (viewerRef.current) {
        const overlayId = e.detail.id;
        viewerRef.current.removeOverlay(overlayId);
      }
    };

    document.addEventListener("overlay-delete", handler, false);
    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("keydown", keyDown);
    document.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      document.removeEventListener("overlay-delete", handler);
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("keydown", keyDown);
      document.removeEventListener("wheel", handleWheel);
    };
  }, [onContextMenu, keyDown, handleWheel]);

  const setClickedPageNumber = useCallback(
    (event) => {
      if (!viewerRef.current || !viewerRef.current.viewport) return;

      const viewportPos = viewerRef.current.viewport.pointFromPixel(event.position);
      const imageIndex = Math.floor(viewportPos.y / (collectionTileSize + collectionTileMargin));
      currentPageNumberRef.current = imageIndex + 1;
    },
    [collectionTileMargin, collectionTileSize]
  );

  return {
    setPageNumber,
    pageNumberAtScrollRef,
    loadingViewer,
    viewer: viewerRef.current,
  };
};

export default useOpenSeaDragon;
